import Vue from 'vue';
import { connectChat, disconnectChat } from '../services/chatSocket';
import Swal from 'sweetalert2';

const axios = require('axios');

import { playNotifSound } from '@/services/notifSound';

// Reactive state
export const chatState = Vue.observable({
  unreadCount: 0,
  unreadByChannel: {},
  connected: false,
});

let initialized = false;
let currentUserId = null;
const channelNames = {};
let avatarMap = {};

async function fetchAvatars() {
  try {
    const apiUrl = process.env.VUE_APP_ROOT_API.replace(/\/+$/, '');
    const token = localStorage.getItem('token');
    const res = await axios.get(apiUrl + '/chat/avatars', {
      headers: { Authorization: 'Bearer ' + token },
    });
    const map = {};
    (res.data || []).forEach((a) => { if (a && a.avatar) map[a.id] = a.avatar; });
    avatarMap = map;
  } catch (e) {
    // brak avatarów nie blokuje powiadomień
  }
}

const originalTitle = document.title;

function updateTabTitle() {
  if (chatState.unreadCount > 0) {
    document.title = `(${chatState.unreadCount}) ${originalTitle}`;
  } else {
    document.title = originalTitle;
  }
}

function playSound() {
  // Dźwięk i głośność wg ustawień użytkownika (Ustawienia konta).
  playNotifSound();
}

// ─── Firmowy toast powiadomień czatu (zamiast generycznego Swal „info") ───
const TOAST_AVATAR_COLORS = [
  '#2C5FF6', '#7C3AED', '#0EA5E9', '#10B981', '#F59E0B',
  '#EF4444', '#EC4899', '#14B8A6', '#6366F1', '#F97316',
];

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function authorColor(id) {
  if (!id) return TOAST_AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return TOAST_AVATAR_COLORS[Math.abs(hash) % TOAST_AVATAR_COLORS.length];
}

function authorInitials(author) {
  if (!author) return 'SY';
  return ((author.firstName || ' ')[0] + ((author.lastName || '')[0] || '')).toUpperCase();
}

let toastStyleInjected = false;
function ensureToastStyle() {
  if (toastStyleInjected) return;
  toastStyleInjected = true;
  const css = `
  .btp-chat-toast.swal2-popup {
    display: flex !important;
    align-items: center;
    gap: 12px;
    width: 360px;
    max-width: calc(100vw - 32px);
    padding: 12px 14px;
    border-radius: 14px;
    background: #16202E;
    color: #E8EDF4;
    box-shadow: 0 10px 30px rgba(0,0,0,0.40);
    border: 1px solid rgba(255,255,255,0.06);
    border-left: 4px solid #2C5FF6;
  }
  .btp-chat-toast .swal2-html-container { margin: 0 !important; padding: 0 !important; width: 100%; text-align: left; }
  .btp-chat-toast .swal2-timer-progress-bar { background: #2C5FF6; }
  .btp-toast { display: flex; align-items: center; gap: 12px; }
  .btp-toast__avatar {
    width: 40px; height: 40px; flex-shrink: 0;
    border-radius: 11px; overflow: hidden;
    display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 700; color: #fff;
  }
  .btp-toast__avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .btp-toast__body { min-width: 0; flex: 1; }
  .btp-toast__top { display: flex; align-items: center; gap: 6px; }
  .btp-toast__name { font-size: 13.5px; font-weight: 700; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .btp-toast__channel { font-size: 11px; color: #8FA3BF; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .btp-toast__text { font-size: 12.5px; color: #B9C6D8; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  `;
  const el = document.createElement('style');
  el.setAttribute('data-btp-chat-toast', '');
  el.textContent = css;
  document.head.appendChild(el);
}

async function joinAllChannels(sock) {
  try {
    const apiUrl = process.env.VUE_APP_ROOT_API.replace(/\/+$/, '');
    const token = localStorage.getItem('token');
    console.log('[GlobalChat] Fetching channels from:', apiUrl + '/chat/channels');
    const res = await axios.get(apiUrl + '/chat/channels', {
      headers: { Authorization: 'Bearer ' + token },
    });
    const channels = res.data || [];
    console.log('[GlobalChat] Joining', channels.length, 'channels');
    channels.forEach(ch => {
      sock.emit('join_channel', { channelId: ch.id });
      channelNames[ch.id] = ch.name;
      console.log('[GlobalChat] Joined channel:', ch.id, ch.name);
    });
  } catch (e) {
    console.error('[GlobalChat] Failed to fetch channels:', e.message);
  }
}

export function initGlobalChat() {
  if (initialized) return;

  const token = localStorage.getItem('token');
  const apiUrl = process.env.VUE_APP_ROOT_API;
  if (!token || !apiUrl) return;

  // Get current user ID
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    currentUserId = payload.user?.id || payload.id || payload.sub;
  } catch (e) {
    // ignore
  }

  const sock = connectChat(token, apiUrl);
  initialized = true;

  sock.on('connect', () => {
    console.log('[GlobalChat] Connected to WebSocket');
    chatState.connected = true;
    joinAllChannels(sock);
    fetchUnreadCounts();
    fetchAvatars();
  });

  sock.on('connect_error', (err) => {
    console.error('[GlobalChat] Connection error:', err.message);
  });

  sock.on('disconnect', (reason) => {
    console.log('[GlobalChat] Disconnected:', reason);
    chatState.connected = false;
  });

  sock.on('new_message', (msg) => {
    console.log('[GlobalChat] New message from:', msg.authorId, 'me:', currentUserId);
    if (msg.authorId === currentUserId) return;

    console.log('[GlobalChat] Playing sound...');
    playSound();

    // Firmowy toast powiadomienia czatu
    ensureToastStyle();
    const authorName = msg.author
      ? (msg.author.firstName + ' ' + msg.author.lastName).trim()
      : 'Nowa wiadomość';
    const preview = msg.type === 'text'
      ? (msg.text || '').substring(0, 70) + ((msg.text || '').length > 70 ? '…' : '')
      : msg.type === 'img' ? '📷 Zdjęcie' : '📎 Plik';
    const channelName = channelNames[msg.channelId] || '';
    const avatarUrl = (msg.author && msg.author.avatar) || avatarMap[msg.authorId] || null;
    const color = authorColor(msg.authorId);
    const avatarInner = avatarUrl
      ? `<img src="${escapeHtml(avatarUrl)}" alt="" />`
      : escapeHtml(authorInitials(msg.author));

    const html =
      `<div class="btp-toast">` +
      `<div class="btp-toast__avatar" style="background:${color}">${avatarInner}</div>` +
      `<div class="btp-toast__body">` +
      `<div class="btp-toast__top">` +
      `<span class="btp-toast__name">${escapeHtml(authorName)}</span>` +
      (channelName ? `<span class="btp-toast__channel">· #${escapeHtml(channelName)}</span>` : '') +
      `</div>` +
      `<div class="btp-toast__text">${escapeHtml(preview)}</div>` +
      `</div></div>`;

    Swal.fire({
      toast: true,
      position: 'top-end',
      html,
      showConfirmButton: false,
      showCloseButton: false,
      timer: 4500,
      timerProgressBar: true,
      customClass: { popup: 'btp-chat-toast' },
      didOpen: (toast) => {
        toast.style.cursor = 'pointer';
        toast.addEventListener('click', () => {
          window.location.href = '/chat';
          Swal.close();
        });
      },
    });

    // Increment per-channel and total
    const onChatPage = window.location.pathname === '/chat';
    if (!onChatPage) {
      const chId = msg.channelId;
      Vue.set(chatState.unreadByChannel, chId, (chatState.unreadByChannel[chId] || 0) + 1);
      chatState.unreadCount = Object.values(chatState.unreadByChannel).reduce((sum, n) => sum + n, 0);
    }

    // Update browser tab title with unread count
    updateTabTitle();
  });

  // Auto-join new channels when they're created
  sock.on('channel_created', (channel) => {
    sock.emit('join_channel', { channelId: channel.id });
  });
}

async function fetchUnreadCounts() {
  try {
    const apiUrl = process.env.VUE_APP_ROOT_API.replace(/\/+$/, '');
    const token = localStorage.getItem('token');
    const res = await axios.get(apiUrl + '/chat/unread', {
      headers: { Authorization: 'Bearer ' + token },
    });
    chatState.unreadByChannel = res.data || {};
    chatState.unreadCount = Object.values(chatState.unreadByChannel).reduce((sum, n) => sum + n, 0);
    console.log('[GlobalChat] Unread counts:', chatState.unreadByChannel, 'total:', chatState.unreadCount);
  } catch (e) {
    console.error('[GlobalChat] Failed to fetch unread counts:', e.message);
  }
}

export function resetUnreadCount() {
  chatState.unreadCount = 0;
  chatState.unreadByChannel = {};
  updateTabTitle();
}

export async function markChannelRead(channelId) {
  try {
    const apiUrl = process.env.VUE_APP_ROOT_API.replace(/\/+$/, '');
    const token = localStorage.getItem('token');
    await axios.post(apiUrl + '/chat/mark-read/' + channelId, {}, {
      headers: { Authorization: 'Bearer ' + token },
    });
    delete chatState.unreadByChannel[channelId];
    chatState.unreadCount = Object.values(chatState.unreadByChannel).reduce((sum, n) => sum + n, 0);
  } catch (e) {
    // ignore
  }
}

export function destroyGlobalChat() {
  disconnectChat();
  initialized = false;
  chatState.unreadCount = 0;
  chatState.connected = false;
}
